import React, { useEffect, useState } from "react";
import {
  Modal,
  ModalContent,
  ModalBody,
  Radio,
  RadioGroup,
  Button,
  addToast
} from "@heroui/react";
import CountDownComponent from "../../common-layout/counter-component";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm, Controller } from "react-hook-form";
import { voteSchema } from "../../../utils/yubSchema/validate";
import { useSelector } from "react-redux";
import { useWallet } from "../../../context/WalletContext";
import { formatUnits } from "ethers";
import { DaoVoteSubmitApi } from "../../../api/methods-marketplace";
import { usePublicClient } from "wagmi";
import { parseAbi } from "viem";

const BAF_TOKEN_ADDRESS = process.env.REACT_APP_BAF_TOKEN_ADDRESS;

const ERC20_ABI = [
  "function balanceOf(address owner) view returns (uint256)",
  "function decimals() view returns (uint8)"
];

const VoteModalPopup = ({ isOpen, onOpenChange, data, onVoted }) => {
  const [votingTimeIsCompleted, setVotingTimeIsCompleted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { isConnected } = useWallet();
  const user = useSelector((state) => state.user);
  const is_custodial_wallet = user?.data?.is_custodial_wallet;
  const user_balance = user?.balance;
  const address = useSelector((state) => state.user?.data?.address);
  const user_id = useSelector((state) => state.user?.data?.id);
  const publicClient = usePublicClient();

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm({
    resolver: yupResolver(voteSchema)
  });

  useEffect(() => {
    if (isOpen && data) {
      reset();
    }
  }, [isOpen, data, reset]);

  const checkBAFToken = async () => {
    if (!isConnected && !address && !is_custodial_wallet) {
      addToast({
        title: "Connect Wallet",
        description: "Please connect your wallet to vote.",
        status: "warning"
      });
      return false;
    }

    if (is_custodial_wallet) {
      const custodialBalance = Number(user_balance ?? 0);

      if (custodialBalance <= 0) {
        addToast({
          title: "Insufficient BAF",
          description: "You need BAF tokens to vote.",
          color: "error",
          variant: "bordered"
        });
        return false;
      }

      return true;
    }

    try {
      const decimals = await publicClient.readContract({
        address: BAF_TOKEN_ADDRESS,
        abi: parseAbi(ERC20_ABI),
        functionName: "decimals"
      });

      const balance = await publicClient.readContract({
        address: BAF_TOKEN_ADDRESS,
        abi: parseAbi(ERC20_ABI),
        functionName: "balanceOf",
        args: [address]
      });

      const formatted = parseFloat(formatUnits(balance, decimals));
      return formatted > 0;
    } catch (err) {
      console.error("Error checking BAF token:", err);
      addToast({
        title: "Token Check Failed",
        description: "Could not verify BAF token balance.",
        status: "error"
      });
      return false;
    }
  };

  const onSubmit = async (formData) => {
    if (!data?.id || !user_id) return;

    setIsSubmitting(true);

    try {
      const hasBAF = await checkBAFToken();
      if (!hasBAF) {
        addToast({
          title: "Token Missing",
          description:
            "You need BAF tokens to submit your vote. Please acquire some before trying again.",
          color: "danger",
          variant: "bordered"
        });
        setIsSubmitting(false);
        return;
      }

      const payload = {
        question_option_user: {
          user_id,
          question_id: data.id,
          option_id: Number(formData.option_id)
        }
      };

      const response = await DaoVoteSubmitApi(payload);

      if (response) {
        addToast({
          title: "Vote Submitted",
          description: "Your vote has been successfully submitted.",
          color: "success",
          variant: "bordered"
        });
        onOpenChange();
        onVoted?.();
      } else {
        addToast({
          title: "Vote Failed",
          description: "Something went wrong. Please try again.",
          color: "danger",
          variant: "bordered"
        });
      }
    } catch (error) {
      addToast({
        title: "Vote Error",
        description: "An error occurred while submitting your vote.",
        color: "danger",
        variant: "bordered"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!data) return null;

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      isDismissable={!isSubmitting}
      isKeyboardDismissDisabled={isSubmitting}
      size="sm"
      scrollBehavior="inside"
      placement="center"
      classNames={{
        closeButton: "hidden"
      }}
    >
      <ModalContent className="max-h-[90vh] overflow-y-auto rounded-2xl">
        <form onSubmit={handleSubmit(onSubmit)}>
          <ModalBody className="p-0 overflow-y-auto">
            <div className="h-6 bg-black px-2 flex items-center justify-center border border-white border-b-0 rounded-t-xl">
              <p className="m-0 text-xs text-center font-normal text-white leading-[1]">
                Voting Ends in:{" "}
                <span className="font-bold">
                  <CountDownComponent
                    date={data.end_date}
                    isSetTime={setVotingTimeIsCompleted}
                  />
                </span>
              </p>
            </div>

            <div className="p-4 flex flex-col gap-5">
              <div className="flex flex-col gap-0.5 border-b border-dashed border-[#C2C2C2] pb-3">
                <h2 className="m-0 text-lg font-medium text-black">
                  {data.name || "N/A"}
                </h2>
                {data.description && (
                  <p className="m-0 text-xs font-normal text-black">
                    {data.description}
                  </p>
                )}
              </div>

              <div className="flex flex-col gap-1">
                <Controller
                  name="option_id"
                  control={control}
                  render={({ field }) => (
                    <RadioGroup
                      label="Select Option:"
                      selectedValue={field.value}
                      onValueChange={field.onChange}
                    >
                      {data.options?.map((item, index) => (
                        <Radio
                          key={item.id || index}
                          value={item.id.toString()}
                          classNames={{
                            control: "bg-black w-4 h-4",
                            wrapper: "!border-black w-6 h-6"
                          }}
                        >
                          {item.name}
                        </Radio>
                      ))}
                    </RadioGroup>
                  )}
                />
                {errors?.option_id && (
                  <small className="text-xs block text-red-600">
                    {errors.option_id.message}
                  </small>
                )}
              </div>

              <div className="flex gap-2.5 items-center">
                <Button
                  type="submit"
                  className="bg-yellowbuy py-1 px-2 text-base leading-[1] h-8 border border-black rounded text-black"
                  radius="none"
                  disabled={votingTimeIsCompleted || isSubmitting}
                  isLoading={isSubmitting}
                >
                  {isSubmitting ? "Saving..." : "Submit"}
                </Button>
                <Button
                  type="button"
                  className="bg-white py-1 px-2 text-base leading-[1] h-8 border border-black rounded text-black"
                  radius="none"
                  onPress={onOpenChange}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </ModalBody>
        </form>
      </ModalContent>
    </Modal>
  );
};

export default VoteModalPopup;
